import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { formatAssistantAppointmentTime } from './AssistantAppointmentCard';
import {
  assistantErrorCode,
  assistantErrorStatus,
  schedulingAssistantApi,
} from './api';
import { writeAssistantOperation } from './operationRecords';
import { assistantMemberPath } from './paths';
import { useEndAssistantSession } from './useAssistantSession';

import type { AssistantAction, AssistantAppointment } from './api';

import { useAppDispatch } from '@/store/hooks';

const KIND_NOUNS: Record<AssistantAppointment['kind'], string> = {
  chemistry: 'Chemistry call',
  coaching: 'Coaching session',
};

export const assistantAppointmentSummary = (
  appointment: AssistantAppointment,
): string => {
  const { date, time } = formatAssistantAppointmentTime(appointment);
  return `${KIND_NOUNS[appointment.kind]} on ${date}, ${time}`;
};

export const newAssistantIdempotencyKey = () =>
  `assistant-${window.crypto.randomUUID()}`;

const samePreimage = (
  seen: Pick<AssistantAppointment, 'seriesMutationVersion' | 'version'>,
  fresh: Pick<AssistantAppointment, 'seriesMutationVersion' | 'version'>,
) =>
  seen.version === fresh.version &&
  seen.seriesMutationVersion === fresh.seriesMutationVersion;

export type AssistantCommandProblem =
  | { kind: 'changed'; appointment: AssistantAppointment }
  | { kind: 'not_allowed' }
  | { kind: 'failed'; code: string | undefined };

/**
 * Builds and records one command. Before anything is written, the appointment
 * is read again: if its versions moved since the assistant looked, nothing is
 * sent and the page shows the change instead. Otherwise the command is stored
 * as `submitting` and the member page sends it (see operationDriver).
 */
export const useAssistantCommand = ({
  action,
  memberId,
  seen,
}: {
  action: AssistantAction;
  memberId: string;
  /** The appointment as the assistant last saw it. */
  seen: AssistantAppointment | undefined;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const endSession = useEndAssistantSession();
  const [isChecking, setIsChecking] = useState(false);
  const [problem, setProblem] = useState<AssistantCommandProblem | null>(null);

  const submit = useCallback(
    async (fields: Record<string, unknown>) => {
      if (!seen) return;
      setIsChecking(true);
      setProblem(null);
      const response = await dispatch(
        schedulingAssistantApi.endpoints.getAssistantAppointment.initiate(
          { appointmentId: seen.id, memberId },
          { forceRefetch: true, subscribe: false },
        ),
      );
      setIsChecking(false);
      if (!response.data) {
        if (assistantErrorStatus(response.error) === 401) {
          endSession('expired');
          return;
        }
        setProblem({
          code: assistantErrorCode(response.error),
          kind: 'failed',
        });
        return;
      }
      const fresh = response.data;
      const allowed =
        action === 'reschedule'
          ? fresh.capabilities.canRescheduleOccurrence
          : fresh.capabilities.canCancelOccurrence;
      if (!allowed) {
        setProblem({ kind: 'not_allowed' });
        return;
      }
      if (!samePreimage(seen, fresh)) {
        setProblem({ appointment: fresh, kind: 'changed' });
        return;
      }
      const idempotencyKey = newAssistantIdempotencyKey();
      const recorded = writeAssistantOperation({
        action,
        appointmentId: fresh.id,
        idempotencyKey,
        memberId,
        operationId: null,
        outcome: null,
        // The order is fixed here and the string is what gets sent and
        // replayed, so a replay is byte-for-byte the first send.
        requestBytes: JSON.stringify({
          expected_series_mutation_version: fresh.seriesMutationVersion,
          expected_version: fresh.version,
          ...fields,
          idempotency_key: idempotencyKey,
        }),
        retryAfterSeconds: null,
        state: 'submitting',
        summary: assistantAppointmentSummary(fresh),
      });
      if (!recorded) {
        setProblem({ code: 'storage_unavailable', kind: 'failed' });
        return;
      }
      navigate(assistantMemberPath(memberId), { replace: true });
    },
    [action, dispatch, endSession, memberId, navigate, seen],
  );

  return { isChecking, problem, submit };
};
